import { useState } from 'react'
import {
  Button,
  Group,
  Text,
  Table,
  Badge,
  Checkbox,
  Collapse,
  ActionIcon,
  Skeleton,
  Stack,
} from '@mantine/core'
import {
  IconPlus,
  IconPencil,
  IconTrash,
  IconChevronDown,
  IconChevronRight,
} from '@tabler/icons-react'
import type { CustomTestPanel } from './customPanelStorage'

interface CustomPanelsSectionProps {
  panels: CustomTestPanel[]
  selectedTests: string[]
  testLookup: Map<string, { code: string; name: string; specimen?: string }>
  onTogglePanel: (testCodes: string[]) => void
  onCreatePanel: () => void
  onEditPanel: (panel: CustomTestPanel) => void
  onDeletePanel: (panelId: string) => void
  loading: boolean
  /**
   * Shared (practice-wide) panels are owned by the organization, not the
   * practitioner, so they can be selected and inspected but never edited,
   * deleted, or added to from the requisition. Authoring shared panels is
   * deliberately out of scope for PD-1479.
   */
  readOnly?: boolean
}

export function CustomPanelsSection({
  panels,
  selectedTests,
  testLookup,
  onTogglePanel,
  onCreatePanel,
  onEditPanel,
  onDeletePanel,
  loading,
  readOnly = false,
}: CustomPanelsSectionProps) {
  const [expandedPanels, setExpandedPanels] = useState<Set<string>>(new Set())

  const toggleExpand = (panelId: string) => {
    setExpandedPanels((prev) => {
      const next = new Set(prev)
      if (next.has(panelId)) next.delete(panelId)
      else next.add(panelId)
      return next
    })
  }

  if (loading) {
    return (
      <Stack gap="xs">
        <Skeleton height={40} />
        <Skeleton height={40} />
      </Stack>
    )
  }

  if (panels.length === 0) {
    return (
      <div className="py-4 text-center">
        <Text size="sm" c="dimmed" mb={readOnly ? undefined : 'sm'}>
          {readOnly
            ? 'No shared panels have been set up for your practice yet.'
            : 'No custom panels yet. Create one to quickly select groups of tests.'}
        </Text>
        {!readOnly && (
          <Button variant="light" leftSection={<IconPlus size={16} />} onClick={onCreatePanel}>
            Create New Panel
          </Button>
        )}
      </div>
    )
  }

  return (
    <Stack gap="xs">
      {panels.map((panel) => {
        const allSelected =
          panel.testCodes.length > 0 &&
          panel.testCodes.every((code) => selectedTests.includes(code))
        const someSelected =
          !allSelected && panel.testCodes.some((code) => selectedTests.includes(code))
        const isExpanded = expandedPanels.has(panel.id)

        return (
          <div key={panel.id} className="rounded-md border border-gray-200">
            <Group
              gap="sm"
              px="sm"
              py="xs"
              wrap="nowrap"
              className="cursor-pointer"
              onClick={() => onTogglePanel(panel.testCodes)}
            >
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                onChange={() => onTogglePanel(panel.testCodes)}
                onClick={(e) => e.stopPropagation()}
              />
              <div className="min-w-0 flex-1">
                <Group gap="xs">
                  <Text size="sm" fw={500} truncate>
                    {panel.name}
                  </Text>
                  <Badge size="xs" variant="light">
                    {panel.testCodes.length} tests
                  </Badge>
                </Group>
              </div>
              <Group gap={4} wrap="nowrap">
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  aria-label={isExpanded ? 'Hide tests' : 'Show tests'}
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleExpand(panel.id)
                  }}
                >
                  {isExpanded ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                </ActionIcon>
                {!readOnly && (
                  <>
                    <ActionIcon
                      variant="subtle"
                      size="sm"
                      aria-label="Edit panel"
                      onClick={(e) => {
                        e.stopPropagation()
                        onEditPanel(panel)
                      }}
                    >
                      <IconPencil size={14} />
                    </ActionIcon>
                    <ActionIcon
                      variant="subtle"
                      size="sm"
                      color="red"
                      aria-label="Delete panel"
                      onClick={(e) => {
                        e.stopPropagation()
                        onDeletePanel(panel.id)
                      }}
                    >
                      <IconTrash size={14} />
                    </ActionIcon>
                  </>
                )}
              </Group>
            </Group>

            <Collapse in={isExpanded}>
              <div className="px-4 pb-2">
                <Table withTableBorder withColumnBorders>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th w={70}>Code</Table.Th>
                      <Table.Th>Test Name</Table.Th>
                      <Table.Th w={90}>Specimen</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {panel.testCodes.map((code) => {
                      const test = testLookup.get(code)
                      return (
                        <Table.Tr key={code}>
                          <Table.Td>
                            <Text size="xs" className="font-mono">
                              {code}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="xs" fw={500}>
                              {test?.name ?? `Unknown test: ${code}`}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Badge size="xs" variant="light" color="gray">
                              {test?.specimen ?? '—'}
                            </Badge>
                          </Table.Td>
                        </Table.Tr>
                      )
                    })}
                  </Table.Tbody>
                </Table>
              </div>
            </Collapse>
          </div>
        )
      })}

      {!readOnly && (
        <Button
          variant="light"
          leftSection={<IconPlus size={16} />}
          onClick={onCreatePanel}
          size="sm"
        >
          Create New Panel
        </Button>
      )}
    </Stack>
  )
}
